import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { getPricing } from '~/Config/Stripe';
import config from '~/Config';
import Button from '~/Components/Global/Form/Button';
import Spinner from '~/Components/Global/Loading/Spinner';
import TotalWithTax from '~/Components/Global/Stripe/TotalWithTax';
import { parseErrors } from '~/Redux/Helpers';
import { subscribe, updateField } from '~/Redux/Modules/Subscription';
import { handleGet } from '~/Redux/Modules/Account';
import { guid, useMountEffect } from '~/util';
import useSetupIntent from '~/Components/Global/Stripe/useSetupIntent';
import Storage from '~/Providers/Storage';
import SetupIntentForm from '~/Components/Pages/Subscription/SetupIntentForm';

import './ManageSubscription.scss';

const stripePromise = loadStripe(config.stripeKey, config.stripeOptions);

const StripeLifetimeUpgrade = ({ modalId, onClose }) => {
  const dispatch = useDispatch();
  const account = useSelector(({ Account }) => Account.data);
  const { modalsOpen } = useSelector(({ Modal }) => Modal);
  const pricingPlans = useCallback(getPricing(), []);
  const selectedPlan = pricingPlans.lifetimePlusInitial;
  const {
    isFetching,
    data: { coupon },
  } = useSelector(({ Subscription }) => Subscription);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);
  const { setupIntent, error: setupIntentError, setSetupIntentId } = useSetupIntent();
  const [postalCode, setPostalCode] = useState('');
  const [eventId, setEventId] = useState(Storage.get('subscriptionEventId'));
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [editPaymentWindowTimer, setEditPaymentWindowTimer] = useState(null);
  const platformNames = {
    ios: 'Apple',
    android: 'Google Play',
  };

  const isOpen = modalsOpen.find(m => m == modalId) != undefined;

  const loadAccount = useCallback(() => {
    dispatch(handleGet()).finally(() => {
      setIsLoadingAccount(false);
    });
  }, [dispatch]);

  useMountEffect(() => {
    if (!eventId) {
      const newId = guid();
      Storage.set('subscriptionEventId', newId);
      setEventId(newId);
    }

    loadAccount();
  });

  useEffect(() => {
    if (isOpen) {
      loadAccount();
    }
  }, [isOpen, loadAccount]);

  useEffect(() => {
    return () => {
      if (editPaymentWindowTimer) {
        clearInterval(editPaymentWindowTimer);
      }
    };
  }, [editPaymentWindowTimer]);

  const handleSetupIntent = () => {
    const editPaymentWindow = window.open(
      `${window.location.origin}/change-payment-source`,
      '_blank',
      'popup,width=400,height=600'
    );

    const timer = setInterval(() => {
      if (editPaymentWindow.closed) {
        setEditingPaymentMethod(null);
        setEditPaymentWindowTimer(null);
        clearInterval(timer);
        loadAccount();
      }
    }, 100);

    setEditingPaymentMethod(editPaymentWindow);
    setEditPaymentWindowTimer(timer);
  };

  const handleFieldChanged = useCallback(
    change => {
      if (change.postal_code) {
        setPostalCode(change.postal_code);
      }
      dispatch(updateField(change));
    },
    [dispatch, setPostalCode]
  );

  const handleSave = () => {
    setErrorMessage('');
    dispatch(
      subscribe(
        {
          paymentMethod: setupIntent.payment_method || { id: null },
          coupon,
          plan: selectedPlan.plan,
          price: selectedPlan.price,
          userId: account.id,
          eventId,
          postalCode: postalCode || account.address_zip,
        },
        true
      )
    )
      .then(() => onClose(true))
      .catch(err => {
        setErrorMessage(parseErrors(err));
      });
  };

  const renderErrorMessage = () => {
    return (
      <>
        <div className="error-message">{errorMessage}</div>
      </>
    );
  };

  const renderSaveButton = () => (
    <Button
      type="button"
      text="SAVE"
      className="pill"
      onClick={() => handleSave()}
      isFetching={isFetching}
      actionText="SAVING"
    />
  );

  const renderLoading = () => (
    <div className="text-center">
      <Spinner />
    </div>
  );

  const renderSubscriptionWarning = () => {
    if (!account?.iapSubscription) return null;

    return (
      <p className="text-center">
        Be sure to cancel your existing{' '}
        {platformNames[account?.iapSubscription?.platform] || 'Apple/Google'} subscription to avoid
        future charges.
      </p>
    );
  };

  const renderCurrentCard = () => {
    if (!account) return null;

    return (
      <div className="current-card-info">
        <span className="current-card-info__text">
          Current Card: {account.card_brand ?? account.pm_type}{' '}
          {account.card_last_four ?? account.pm_last_four}
        </span>
        {editingPaymentMethod ? (
          <span className="current-card-info__edit">
            <Spinner />
          </span>
        ) : (
          <span
            className="current-card-info__edit"
            onClick={() => handleSetupIntent()}
            role="button"
            tabIndex="0"
          >
            Change...
          </span>
        )}
      </div>
    );
  };

  const renderStripeForm = () => {
    if (!setupIntent || !selectedPlan) return null;

    return (
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret: setupIntent.clientSecret,
          appearance: { variables: { fontSizeBase: '14px' } },
          loader: 'always',
        }}
        key={setupIntent.clientSecret}
      >
        <SetupIntentForm
          isFetching={isFetching}
          user={account}
          intent={setupIntent}
          setSetupIntentId={setSetupIntentId}
          amount={selectedPlan.price}
          plan={selectedPlan.plan}
          coupon={coupon}
          updateField={handleFieldChanged}
          postalCode={postalCode}
        />
      </Elements>
    );
  };

  const renderSetupError = () => {
    if (!setupIntentError) return null;

    return (
      <div className="text-danger">
        An error occurred while setting up payments. Please try again later.
      </div>
    );
  };

  const renderStripeLoading = () => {
    if (setupIntent || setupIntentError) return null;

    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  };

  const renderHiddenInputs = () => (
    <>
      <input id="artfulUserId" type="hidden" name="userId" value={account.id} />
      <input id="artfulEventId" type="hidden" name="eventId" value={eventId} />
    </>
  );

  const renderForm = () => {
    const hasSavedCard = account?.card_brand || account?.pm_type;

    return (
      <>
        {renderSubscriptionWarning()}
        {hasSavedCard ? renderCurrentCard() : renderStripeForm()}
        {!hasSavedCard && renderSetupError()}
        {!hasSavedCard && renderStripeLoading()}
        {renderHiddenInputs()}
        {hasSavedCard && (
          <div className="price-display">
            <TotalWithTax
              amount={selectedPlan.price}
              zip={account?.address_zip}
              coupon={coupon}
              plan={selectedPlan.plan}
            />
          </div>
        )}
        {errorMessage && renderErrorMessage()}
        <div className="actions">{renderSaveButton()}</div>
      </>
    );
  };

  return (
    <>
      <h1>Upgrade to Lifetime Access</h1>
      <p className="sub-header">(Includes Artful Plus Features)</p>
      <p>
        Upgrade now to begin your lifetime access. Artful Plus features will be unlocked
        immediately!
      </p>
      {isLoadingAccount ? renderLoading() : renderForm()}
    </>
  );
};

StripeLifetimeUpgrade.propTypes = {
  modalId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default StripeLifetimeUpgrade;
